// In-browser data chart renderer (Chart.js) with invalid-data placeholder + regenerate.

import React from "react";
import {
  Chart as ChartJS,
  registerables,
  type ChartConfiguration,
} from "chart.js";
import type { Chart as ChartModel } from "@asmg/shared";
import { api } from "../api/client";
import { Button, ErrorBanner } from "../components/ui";

ChartJS.register(...registerables);

function buildConfig(chart: ChartModel): ChartConfiguration {
  const { kind, data, title } = chart;
  const legend = { display: data.series.length > 1 };
  const base = { responsive: true, maintainAspectRatio: false, plugins: { legend } };

  if (kind === "pie") {
    const s = data.series[0] ?? { values: [] };
    return { type: "pie", data: { labels: data.labels, datasets: [{ data: s.values }] }, options: base };
  }
  if (kind === "waterfall") {
    const vals = (data.series[0] ?? { values: [] }).values;
    const bases: number[] = [];
    let run = 0;
    for (const v of vals) {
      bases.push(run);
      run += v;
    }
    return {
      type: "bar",
      data: {
        labels: data.labels,
        datasets: [
          { label: "base", data: bases, backgroundColor: "rgba(0,0,0,0)", stack: "w" },
          { label: title ?? "Value", data: vals, stack: "w" },
        ],
      },
      options: { ...base, scales: { x: { stacked: true }, y: { stacked: true } } },
    };
  }
  const datasets = data.series.map((s, i) => ({ label: s.name ?? `Series ${i + 1}`, data: s.values }));
  return {
    type: "bar",
    data: { labels: data.labels, datasets },
    options: kind === "timeline" ? { ...base, indexAxis: "y" as const } : base,
  };
}

export function ChartView({
  projectId,
  chart,
  onRegenerated,
  readOnly = false,
}: {
  projectId: string;
  chart: ChartModel;
  onRegenerated: (c: ChartModel) => void;
  readOnly?: boolean;
}) {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const instRef = React.useRef<ChartJS | null>(null);
  const [instruction, setInstruction] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<unknown>(null);

  React.useEffect(() => {
    if (!chart.isValid || !canvasRef.current) return;
    instRef.current?.destroy();
    instRef.current = new ChartJS(canvasRef.current, buildConfig(chart));
    return () => {
      instRef.current?.destroy();
      instRef.current = null;
    };
  }, [chart]);

  async function regenerate() {
    setBusy(true);
    setError(null);
    try {
      const updated = await api.regenerateChart(projectId, chart.id, instruction.trim() || undefined);
      onRegenerated(updated); // prior preserved on failure (server-side)
      setInstruction("");
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="chart-wrap" style={{ margin: "16px 0" }}>
      {chart.title && <div style={{ fontWeight: 600, marginBottom: 6 }}>{chart.title}</div>}
      {chart.isValid ? (
        <div className="chart-box">
          <canvas ref={canvasRef} />
        </div>
      ) : (
        <div className="chart-placeholder">
          Chart “{chart.title || chart.kind}” could not be rendered (invalid data).
        </div>
      )}
      {!readOnly && (
        <>
          <ErrorBanner error={error} onRetry={regenerate} />
          <div className="row" style={{ marginTop: 8 }}>
            <input
              className="grow"
              placeholder="Regenerate instruction (optional) e.g. use realistic values"
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
            />
            <Button variant="ghost" onClick={regenerate} loading={busy}>
              Regenerate Chart
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
